import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Card } from '@/shared/components/ui/card'
import { Search, X } from 'lucide-react'
import type { GroupExtensionsFilters } from '@/modules/users/types/group'

type FilterField = 'permiso' | 'cliente'

interface GroupsFiltersProps {
  filters: GroupExtensionsFilters
  onFiltersChange: (filters: GroupExtensionsFilters) => void
  onClearFilters: () => void
  filterField?: FilterField
}

const filterLabels: Record<FilterField, { all: string; yes: string; no: string; chip: string }> = {
  permiso: {
    all: 'Todos',
    yes: 'Con permiso especial',
    no: 'Sin permiso especial',
    chip: 'Permiso',
  },
  cliente: {
    all: 'Todos',
    yes: 'Clientes activos',
    no: 'No clientes',
    chip: 'Cliente',
  },
}

export function GroupsFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  filterField = 'permiso',
}: GroupsFiltersProps) {
  const labels = filterLabels[filterField]
  const filterValue = filters[filterField]

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined, page: 1 })
  }

  const handleFilterChange = (value: string) => {
    onFiltersChange({
      ...filters,
      [filterField]: value === 'all' ? undefined : value === 'yes',
      page: 1,
    })
  }

  const hasActiveFilters = filters.search || filterValue !== undefined

  return (
    <Card className="p-4 border border-gray-300 dark:border-gray-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre de grupo..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filter */}
          <Select
            value={
              filterValue === undefined ? 'all' : filterValue ? 'yes' : 'no'
            }
            onValueChange={handleFilterChange}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder={labels.chip} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{labels.all}</SelectItem>
              <SelectItem value="yes">{labels.yes}</SelectItem>
              <SelectItem value="no">{labels.no}</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="default"
              onClick={onClearFilters}
              className="whitespace-nowrap"
            >
              <X className="mr-2 h-4 w-4" />
              Limpiar
            </Button>
          )}
        </div>

        {/* Active Filters Chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>Filtros activos:</span>
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 dark:text-teal-400">
                Búsqueda: "{filters.search}"
              </span>
            )}
            {filterValue !== undefined && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-700 dark:text-teal-400">
                {labels.chip}: {filterValue ? 'Sí' : 'No'}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
